/**
 * Pump management hub for super admin — settings, users, billing, analytics, impersonation.
 */
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import { API_URL } from '../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import {
  adminAddSubscription,
  adminActivatePumpAccess,
  adminImpersonate,
  adminMarkSubscriptionPaid,
  adminRenewSubscription,
  adminResetPassword,
  adminSetUserLock,
  applyImpersonation,
  fetchAdminPlans,
  fetchAdminPumpDetail,
  fetchAdminPumpAnalytics,
  fetchPumpInvoiceSettings,
  savePumpInvoiceSettings,
  savePumpMaintenance,
} from './adminApi';
import InvoiceSettingsFormFields from '../shared/InvoiceSettingsFormFields';
import AdminPumpInvoicesPanel from './AdminPumpInvoicesPanel';
import AdminPumpAccountingPanel from './AdminPumpAccountingPanel';
import AdminPumpOwnerFundAnalyticsPanel from './AdminPumpOwnerFundAnalyticsPanel';
import AdminPumpIntelligenceModule from './AdminPumpIntelligenceModule';
import AdminPumpSectionShell from './AdminPumpSectionShell';
import AdminPumpAnalyticsDateBar from './AdminPumpAnalyticsDateBar';
import AdminPumpHubHeader from './AdminPumpHubHeader';
import AdminPumpHubSidebar from './AdminPumpHubSidebar';
import AdminPumpOverviewDashboard from './AdminPumpOverviewDashboard';
import AdminPumpHubFocusBar from './AdminPumpHubFocusBar';
import { MODULE_META, moduleNeedsDateRange, INTELLIGENCE_MODULE_IDS } from './adminPumpHubConfig';
import { defaultAnalyticsRange, matchAnalyticsPreset, buildAnalyticsPresets } from './adminAnalyticsDateUtils';
import { formatInrAmount } from '../../../utils/reportFormat';
import { NumTd, NumTh } from '../../../utils/reportDisplay';
import {
  BILLING_MONTH_OPTIONS,
  addMonthsToDateInput,
  quotePlanPurchase,
} from '../Manager/SubscriptionManagement/subscriptionApi';
import {
  DEFAULT_INVOICE_SETTINGS,
  applyInvoiceFieldUpdates,
  mapInvoiceSettingsFromApi,
  syncInvoiceGstFields,
  validateInvoiceSettings,
} from '../../../utils/invoiceSettingsForm';
import AccountingFixProcessingOverlay, { useAccountingProcessingGuard } from './AccountingFixProcessingOverlay';
import './admin-dashboard.css';
import './admin-pump-hub.css';

function todayDateString() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const AdminPumpDetail = (props) => {
  const { pumpId } = useParams();
  const history = useHistory();
  const [activeModule, setActiveModule] = useState('dashboard');
  const [detail, setDetail] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [subscriptionStartDate, setSubscriptionStartDate] = useState(todayDateString);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState('');
  const [billingMonths, setBillingMonths] = useState(1);
  const [lockStart, setLockStart] = useState('');
  const [lockEnd, setLockEnd] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [opFromDate, setOpFromDate] = useState('');
  const [opToDate, setOpToDate] = useState('');
  const [analyticsDraftFrom, setAnalyticsDraftFrom] = useState('');
  const [analyticsDraftTo, setAnalyticsDraftTo] = useState('');
  const [analyticsAppliedFrom, setAnalyticsAppliedFrom] = useState('');
  const [analyticsAppliedTo, setAnalyticsAppliedTo] = useState('');
  const [activeAnalyticsPreset, setActiveAnalyticsPreset] = useState('last90');
  const [analyticsRangeKey, setAnalyticsRangeKey] = useState(0);
  const [invoice, setInvoice] = useState(DEFAULT_INVOICE_SETTINGS);
  const [invoiceErrors, setInvoiceErrors] = useState({});
  const [invoiceSection, setInvoiceSection] = useState('settings');
  const [platformNavVisible, setPlatformNavVisible] = useState(false);
  const [hubNavVisible, setHubNavVisible] = useState(false);
  const [impersonatingUser, setImpersonatingUser] = useState(null);
  const [analyticsSummaryLoading, setAnalyticsSummaryLoading] = useState(false);
  const [analyticsSummary, setAnalyticsSummary] = useState({
    loaded: false,
    overall_health_score: null,
    health_label: null,
    verdict: null,
    radar: [],
  });

  const handleAnalyticsSummaryLoaded = useCallback((summary) => {
    if (summary) {
      setAnalyticsSummary(summary);
      setAnalyticsSummaryLoading(false);
    }
  }, []);

  const loadAnalyticsSummary = useCallback((fromDate, toDate) => {
    if (!fromDate || !toDate || !pumpId) {
      return;
    }

    setAnalyticsSummaryLoading(true);
    fetchAdminPumpAnalytics(pumpId, fromDate, toDate)
      .then((data) => {
        if (data?.status === false) {
          setAnalyticsSummary({
            loaded: false,
            overall_health_score: null,
            health_label: null,
            verdict: null,
            radar: [],
          });
          return;
        }

        const executiveSummary = data?.executive_summary;
        if (!executiveSummary) {
          setAnalyticsSummary({
            loaded: false,
            overall_health_score: null,
            health_label: null,
            verdict: null,
            radar: [],
          });
          return;
        }

        setAnalyticsSummary({
          loaded: true,
          overall_health_score: executiveSummary.overall_health_score ?? 0,
          health_label: executiveSummary.health_label ?? 'Unknown',
          verdict: executiveSummary.verdict ?? '',
          from_date: data.from_date ?? fromDate,
          to_date: data.to_date ?? toDate,
          radar: Array.isArray(data.radar) ? data.radar : [],
        });
      })
      .catch(() => {
        setAnalyticsSummary({
          loaded: false,
          overall_health_score: null,
          health_label: null,
          verdict: null,
          radar: [],
        });
      })
      .finally(() => setAnalyticsSummaryLoading(false));
  }, [pumpId]);

  useEffect(() => {
    checkAccess();
  }, [pumpId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const module = params.get('module');
    const section = params.get('section');
    if (module) {
      setActiveModule(module);
    }
    if (section === 'invoices') {
      setInvoiceSection('invoices');
    }
  }, [pumpId]);

  useEffect(() => {
    if (!analyticsAppliedFrom || !analyticsAppliedTo) {
      return;
    }
    loadAnalyticsSummary(analyticsAppliedFrom, analyticsAppliedTo);
  }, [analyticsAppliedFrom, analyticsAppliedTo, analyticsRangeKey, loadAnalyticsSummary]);

  function checkAccess() {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => {
        if (profile) {
          loadDetail();
          fetchAdminPlans().then((res) => setPlans(res.plans || [])).catch(() => {});
        } else {
          history.goBack();
        }
      })
      .catch(() => history.goBack());
  }

  function loadDetail() {
    setLoading(true);
    fetchAdminPumpDetail(pumpId)
      .then((data) => {
        if (!data.status) {
          swal('Error', data.message || 'Pump not found', 'error');
          history.push('/admin_dashboard');
          return;
        }
        setDetail(data);
        localStorage.setItem('admin_pump_id', pumpId);
        setLockStart(data.lock_dates?.ac_loc_sys_start_date || '');
        setLockEnd(data.lock_dates?.ac_loc_sys_end_date || '');
        setMaintenanceMode(!!data.maintenance?.maintenance_mode);
        setMaintenanceMessage(data.maintenance?.maintenance_message || '');
        const opFrom = data.operation_dates?.from_date || '';
        const opTo = data.operation_dates?.to_date || '';
        setOpFromDate(opFrom);
        setOpToDate(opTo);
        const defaultRange = defaultAnalyticsRange(opFrom, opTo);
        setAnalyticsDraftFrom(defaultRange.from_date);
        setAnalyticsDraftTo(defaultRange.to_date);
        setAnalyticsAppliedFrom(defaultRange.from_date);
        setAnalyticsAppliedTo(defaultRange.to_date);
        setActiveAnalyticsPreset(matchAnalyticsPreset(buildAnalyticsPresets(opFrom, opTo), defaultRange.from_date, defaultRange.to_date) || 'last90');
        setAnalyticsRangeKey((k) => k + 1);
      })
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (activeModule === 'invoice') {
      fetchPumpInvoiceSettings(pumpId).then((res) => {
        if (res.status) {
          setInvoice(mapInvoiceSettingsFromApi(res.data));
          setInvoiceErrors({});
        }
      });
    }
  }, [activeModule]);

  function saveLockDates() {
    fetch(`${API_URL}/save-pump-lock-dates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('AUTH_TOKEN'),
      },
      body: JSON.stringify({
        pump_id: pumpId,
        ac_loc_sys_start_date: lockStart,
        ac_loc_sys_end_date: lockEnd,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status) swal('Saved', 'Lock dates updated', 'success');
        else swal('Error', data.message || 'Save failed', 'error');
      });
  }

  function saveMaintenanceSettings() {
    savePumpMaintenance(pumpId, {
      maintenance_mode: !!maintenanceMode,
      maintenance_message: maintenanceMessage || '',
    })
      .then((data) => {
        if (data.status) {
          swal('Saved', data.message || 'Maintenance settings updated', 'success');
          setMaintenanceMode(!!data.data?.maintenance_mode);
          setMaintenanceMessage(data.data?.maintenance_message || '');
        } else {
          swal('Error', data.message || 'Save failed', 'error');
        }
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function updateSubscriptionEndDate(planId, startDate, months = billingMonths) {
    setSubscriptionEndDate(addMonthsToDateInput(startDate, months));
  }

  function handlePlanChange(event) {
    const planId = event.target.value;
    setSelectedPlanId(planId);
    updateSubscriptionEndDate(planId, subscriptionStartDate, billingMonths);
  }

  function handleSubscriptionStartChange(event) {
    const startDate = event.target.value;
    setSubscriptionStartDate(startDate);
    updateSubscriptionEndDate(selectedPlanId, startDate, billingMonths);
  }

  function handleBillingMonthsChange(months) {
    const next = Math.min(36, Math.max(1, parseInt(months, 10) || 1));
    setBillingMonths(next);
    updateSubscriptionEndDate(selectedPlanId, subscriptionStartDate, next);
  }

  function handleAddSubscription() {
    if (!selectedPlanId) {
      swal('Select a plan', 'Choose a subscription plan first', 'warning');
      return;
    }
    if (!subscriptionStartDate) {
      swal('Start date required', 'Select a subscription start date', 'warning');
      return;
    }
    adminAddSubscription(pumpId, {
      plan_id: selectedPlanId,
      start_date: subscriptionStartDate,
      billing_months: billingMonths,
      auto_approve_invoice: true,
    })
      .then((res) => {
        swal('Success', res.message || 'Subscription added', 'success');
        setSelectedPlanId('');
        setSubscriptionStartDate(todayDateString());
        setSubscriptionEndDate('');
        setBillingMonths(1);
        loadDetail();
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function handleActivateAccess(waived = true) {
    const title = waived ? 'Activate this pump?' : 'Remove complimentary access?';
    const text = waived
      ? 'The owner will be able to use Petrosmart without an online payment. You can still add a paid subscription later.'
      : 'The owner will need an active subscription before they can use the app again.';
    swal({ title, text, icon: 'warning', buttons: true }).then((ok) => {
      if (!ok) return;
      adminActivatePumpAccess(pumpId, waived)
        .then((res) => {
          swal('Success', res.message || 'Access updated', 'success');
          loadDetail();
        })
        .catch((err) => swal('Error', err.message, 'error'));
    });
  }

  function handleRenew(subscriptionId) {
    adminRenewSubscription(subscriptionId)
      .then((res) => {
        const draft = res.draft_invoice;
        const message = draft
          ? `Renewed. Draft invoice #${draft.id} saved — approve under Invoice → Drafts & receipts.`
          : res.message;
        swal('Success', message, 'success');
        loadDetail();
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function handleMarkPaid(subscriptionId) {
    adminMarkSubscriptionPaid(subscriptionId)
      .then((res) => {
        swal('Success', res.message, 'success');
        loadDetail();
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function handleResetPassword(user) {
    swal({
      title: `Reset password for ${user.name}`,
      content: {
        element: 'input',
        attributes: { placeholder: 'New password (min 8 chars)', type: 'password' },
      },
      buttons: true,
    }).then((value) => {
      if (!value || value.length < 8) return;
      adminResetPassword(user.id, value)
        .then((res) => swal('Success', res.message, 'success'))
        .catch((err) => swal('Error', err.message, 'error'));
    });
  }

  function handleLockUser(user, locked) {
    adminSetUserLock(user.id, locked)
      .then((res) => {
        swal('Success', res.message, 'success');
        loadDetail();
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  useAccountingProcessingGuard(
    !!impersonatingUser,
    'Opening pump as user — please wait until the page loads.'
  );

  function handleImpersonate(user) {
    if (impersonatingUser) {
      return;
    }

    swal({
      title: `View as ${user.name}?`,
      text: 'You will see the pump dashboard with this user\'s permissions. You can return to super admin anytime.',
      icon: 'warning',
      buttons: true,
    }).then((ok) => {
      if (!ok) return;
      setImpersonatingUser({ id: user.id, name: user.name || user.user_name });
      adminImpersonate(user.id)
        .then((res) => {
          applyImpersonation(res.impersonation, res.original_admin?.user_name);
          const screen = res.impersonation.last_screen || 'dashboard';
          window.location.href = '/' + screen.replace(/^\//, '');
        })
        .catch((err) => {
          setImpersonatingUser(null);
          swal('Error', err.message, 'error');
        });
    });
  }

  function applyAnalyticsDateRange() {
    if (!analyticsDraftFrom || !analyticsDraftTo || analyticsDraftFrom > analyticsDraftTo) {
      return;
    }
    setAnalyticsAppliedFrom(analyticsDraftFrom);
    setAnalyticsAppliedTo(analyticsDraftTo);
    setActiveAnalyticsPreset(matchAnalyticsPreset(buildAnalyticsPresets(opFromDate, opToDate), analyticsDraftFrom, analyticsDraftTo));
    setAnalyticsRangeKey((k) => k + 1);
  }

  function applyAnalyticsPreset(preset) {
    if (!preset?.from_date || !preset?.to_date) {
      return;
    }
    setAnalyticsDraftFrom(preset.from_date);
    setAnalyticsDraftTo(preset.to_date);
    setAnalyticsAppliedFrom(preset.from_date);
    setAnalyticsAppliedTo(preset.to_date);
    setActiveAnalyticsPreset(preset.key);
    setAnalyticsRangeKey((k) => k + 1);
  }

  function handleAnalyticsDraftFromChange(value) {
    setAnalyticsDraftFrom(value);
    setActiveAnalyticsPreset('');
  }

  function handleAnalyticsDraftToChange(value) {
    setAnalyticsDraftTo(value);
    setActiveAnalyticsPreset('');
  }

  function launchModule(moduleId) {
    setActiveModule(moduleId);
    if (INTELLIGENCE_MODULE_IDS.includes(moduleId)) {
      setHubNavVisible(false);
    }
  }

  function openReportsModule() {
    setActiveModule('owner-dashboard');
    setHubNavVisible(false);
  }

  function handleInvoiceChange(nameOrPatch, value) {
    setInvoice((prev) => applyInvoiceFieldUpdates(prev, nameOrPatch, value));
    setInvoiceErrors((prev) => {
      if (nameOrPatch && typeof nameOrPatch === 'object' && !Array.isArray(nameOrPatch)) {
        const next = { ...prev };
        Object.keys(nameOrPatch).forEach((key) => {
          next[key] = null;
        });
        return next;
      }
      return { ...prev, [nameOrPatch]: null };
    });
  }

  function saveInvoice() {
    const synced = syncInvoiceGstFields(invoice);
    setInvoice(synced);
    const errors = validateInvoiceSettings(synced);
    setInvoiceErrors(errors);
    if (Object.keys(errors).length > 0) {
      swal('Validation', 'Please fix the highlighted invoice fields', 'warning');
      return;
    }

    savePumpInvoiceSettings(pumpId, synced)
      .then((res) => {
        if (res.status) {
          swal('Success', res.message, 'success');
        } else {
          swal('Error', res.message || 'Save failed', 'error');
        }
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  if (loading || !detail) {
    return (
      <div className="main-container page-wrap">
        <div className="text-center py-5">Loading pump details...</div>
      </div>
    );
  }

  const pump = detail.pump;
  const billing = detail.billing || {};
  const selectedAddPlan = plans.find((item) => String(item.id) === String(selectedPlanId));
  const addQuote = selectedAddPlan ? quotePlanPurchase(selectedAddPlan, billingMonths) : null;
  const analyticsDateRange = analyticsAppliedFrom && analyticsAppliedTo
    ? { from_date: analyticsAppliedFrom, to_date: analyticsAppliedTo }
    : null;
  const showAnalyticsDateBar = moduleNeedsDateRange(activeModule);
  const sectionMeta = MODULE_META[activeModule] || MODULE_META.dashboard;

  return (
    <div className={`main-container page-wrap admin-platform admin-pump-hub ${!platformNavVisible ? 'admin-pump-hub--focus-platform' : ''}`}>
      <AccountingFixProcessingOverlay
        visible={!!impersonatingUser}
        title="Opening pump as user"
        message={`Signing you in as ${impersonatingUser?.name || 'this user'}.`}
        livePhase="Loading permissions and preparing the pump dashboard…"
        safetyNote="Large pumps can take up to a minute while the session is prepared."
      />
      <div className="d-md-flex admin-pump-hub__shell">
        {platformNavVisible && <Sidebar_Progress props={props} progress={23} />}
        <div className="content-area admin-pump-hub__content-area">
          <LoginHeaderOperational props={props} page_title={pump.pump_name} />
          <div className="container-fluid pb-4 px-3 px-lg-4">
            <AdminPumpHubHeader
              pump={pump}
              analyticsSummary={analyticsSummary}
              analyticsSummaryLoading={analyticsSummaryLoading}
              billing={billing}
              userCount={detail.users?.length || 0}
              alertCount={detail.alerts?.length || 0}
              onOpenReports={openReportsModule}
            />

            <AdminPumpHubFocusBar
              platformNavVisible={platformNavVisible}
              hubNavVisible={hubNavVisible}
              onTogglePlatformNav={() => setPlatformNavVisible((v) => !v)}
              onToggleHubNav={() => setHubNavVisible((v) => !v)}
              activeModuleLabel={sectionMeta.title}
            />

            {showAnalyticsDateBar && (
              <div className="admin-pump-hub__date-bar">
                <AdminPumpAnalyticsDateBar
                  operationFrom={opFromDate}
                  operationTo={opToDate}
                  draftFrom={analyticsDraftFrom}
                  draftTo={analyticsDraftTo}
                  appliedFrom={analyticsAppliedFrom}
                  appliedTo={analyticsAppliedTo}
                  activePresetKey={activeAnalyticsPreset}
                  onDraftFromChange={handleAnalyticsDraftFromChange}
                  onDraftToChange={handleAnalyticsDraftToChange}
                  onApply={applyAnalyticsDateRange}
                  onPreset={applyAnalyticsPreset}
                />
              </div>
            )}

            <div className={`admin-pump-hub__layout ${!hubNavVisible ? 'admin-pump-hub__layout--full' : ''}`}>
              {hubNavVisible && (
                <AdminPumpHubSidebar
                  activeModule={activeModule}
                  onSelectModule={setActiveModule}
                />
              )}

              <main className="admin-pump-hub__main">
                {INTELLIGENCE_MODULE_IDS.includes(activeModule) && activeModule !== 'accounting' && (
                  <AdminPumpIntelligenceModule
                    moduleId={activeModule}
                    pumpId={pumpId}
                    pumpName={pump.pump_name}
                    syncDateRange={analyticsDateRange}
                    dateRangeKey={analyticsRangeKey}
                    onReportLoaded={handleAnalyticsSummaryLoaded}
                  />
                )}

                {activeModule === 'dashboard' && (
                  <AdminPumpOverviewDashboard
                    analyticsSummary={analyticsSummary}
                    analyticsSummaryLoading={analyticsSummaryLoading}
                    lockStart={lockStart}
                    lockEnd={lockEnd}
                    onLockStartChange={setLockStart}
                    onLockEndChange={setLockEnd}
                    onSaveLockDates={saveLockDates}
                    maintenanceMode={maintenanceMode}
                    maintenanceMessage={maintenanceMessage}
                    onMaintenanceModeChange={setMaintenanceMode}
                    onMaintenanceMessageChange={setMaintenanceMessage}
                    onSaveMaintenance={saveMaintenanceSettings}
                    onLaunchModule={launchModule}
                  />
                )}

                {activeModule === 'accounting' && (
                  <AdminPumpSectionShell
                    step={5}
                    title={MODULE_META.accounting.title}
                    description={MODULE_META.accounting.description}
                  >
                    <AdminPumpAccountingPanel
                      pumpId={pumpId}
                      pumpName={pump.pump_name}
                      defaultFromDate={analyticsAppliedFrom || opFromDate}
                      defaultToDate={analyticsAppliedTo || opToDate}
                      onOpenOwnerFundAnalytics={() => setActiveModule('owner-fund-analytics')}
                    />
                  </AdminPumpSectionShell>
                )}

                {activeModule === 'owner-fund-analytics' && (
                  <AdminPumpSectionShell
                    step={6}
                    title={MODULE_META['owner-fund-analytics'].title}
                    description={MODULE_META['owner-fund-analytics'].description}
                  >
                    <AdminPumpOwnerFundAnalyticsPanel
                      pumpId={pumpId}
                      pumpName={pump.pump_name}
                      defaultFromDate={analyticsAppliedFrom || opFromDate}
                      defaultToDate={analyticsAppliedTo || opToDate}
                      onOpenAccounting={() => setActiveModule('accounting')}
                    />
                  </AdminPumpSectionShell>
                )}

                {activeModule === 'users' && (
                  <AdminPumpSectionShell
                    title={MODULE_META.users.title}
                    description={MODULE_META.users.description}
                    feedback={{
                      tone: 'info',
                      title: 'Open pump as user',
                      message: 'Use "Open pump as this user" to see exactly what that staff member sees — useful for support and training.',
                    }}
                  >
                  <div className="admin-pump-hub__panel">
                    <div className="table-responsive">
                      <table className="table admin-pump-table mb-0">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Login</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.users || []).map((user) => (
                            <tr key={user.id}>
                              <td>{user.name}</td>
                              <td>{user.user_name}</td>
                              <td>{user.role?.role_name || '-'}</td>
                              <td>
                                <span className={`admin-badge ${user.status === 1 ? 'active' : 'inactive'}`}>
                                  {user.status === 1 ? 'Active' : 'Locked'}
                                </span>
                              </td>
                              <td>
                                <div className="admin-action-row">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary admin-pump-impersonate-btn"
                                    onClick={() => handleImpersonate(user)}
                                    disabled={!!impersonatingUser}
                                    aria-busy={impersonatingUser?.id === user.id}
                                  >
                                    {impersonatingUser?.id === user.id ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        Opening pump…
                                      </>
                                    ) : (
                                      'Open pump as this user'
                                    )}
                                  </button>
                                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleResetPassword(user)}>
                                    Reset password
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleLockUser(user, user.status === 1)}
                                  >
                                    {user.status === 1 ? 'Lock' : 'Unlock'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  </AdminPumpSectionShell>
                )}

                {activeModule === 'subscription' && (
                  <AdminPumpSectionShell
                    title={MODULE_META.subscription.title}
                    description={MODULE_META.subscription.description}
                  >
                  <div className="admin-pump-hub__panel">
                    {detail.subscription_access?.required && (
                      <div className={`alert ${detail.subscription_access.blocked ? 'alert-warning' : 'alert-success'} py-2 px-3 mb-3`}>
                        {detail.subscription_access.blocked
                          ? 'This newly registered pump cannot use Petrosmart until you add a subscription or activate access.'
                          : detail.subscription_access.waived
                            ? 'Complimentary access is active. The owner can use Petrosmart without an online payment.'
                            : 'This pump has an active subscription.'}
                        <div className="mt-2">
                          {detail.subscription_access.blocked ? (
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleActivateAccess(true)}>
                              Activate access
                            </button>
                          ) : detail.subscription_access.waived ? (
                            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleActivateAccess(false)}>
                              Remove complimentary access
                            </button>
                          ) : null}
                        </div>
                      </div>
                    )}
                    <div className="row mb-3 g-2 align-items-end">
                      <div className="col-md-4">
                        <label className="form-label small">Subscription plan</label>
                        <select className="form-select form-select-sm" value={selectedPlanId} onChange={handlePlanChange}>
                          <option value="">Select plan to add</option>
                          {plans.map((plan) => (
                            <option key={plan.id} value={plan.id}>
                              {plan.plan_name} — {formatInrAmount(plan.amount)} / {plan.duration} days
                              {plan.max_nozzles ? ` · ≤${plan.max_nozzles} nozzles` : ''}
                              {plan.max_tanks ? ` · ≤${plan.max_tanks} tanks` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small">Months</label>
                        <input
                          type="number"
                          min="1"
                          max="36"
                          className="form-control form-control-sm"
                          value={billingMonths}
                          onChange={(e) => handleBillingMonthsChange(e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small">Start date</label>
                        <input type="date" className="form-control form-control-sm" value={subscriptionStartDate} onChange={handleSubscriptionStartChange} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small">End date</label>
                        <input type="date" className="form-control form-control-sm bg-light" value={subscriptionEndDate} readOnly tabIndex={-1} aria-label="Calculated subscription end date" />
                      </div>
                      <div className="col-md-2">
                        <button type="button" className="btn btn-primary btn-sm" onClick={handleAddSubscription}>Add subscription</button>
                      </div>
                    </div>
                    <div className="admin-sub-month-pills mb-2">
                      {BILLING_MONTH_OPTIONS.map((months) => {
                        const offer = (selectedAddPlan?.advance_offers || []).find((row) => Number(row.months) === months);
                        return (
                          <button
                            key={months}
                            type="button"
                            className={`admin-sub-month-pill ${Number(billingMonths) === months ? 'is-selected' : ''}`}
                            onClick={() => handleBillingMonthsChange(months)}
                          >
                            {months} mo
                            {offer ? <span>{offer.discount_percent}% off</span> : null}
                          </button>
                        );
                      })}
                    </div>
                    {selectedPlanId && subscriptionEndDate && (
                      <p className="small text-muted mb-3">
                        End date is {billingMonths} month{Number(billingMonths) === 1 ? '' : 's'} from the start date
                        {addQuote
                          ? ` · payable ${formatInrAmount(addQuote.payable_amount)}${addQuote.discount_percent ? ` (${addQuote.discount_percent}% advance discount)` : ''}`
                          : ''}
                        . A draft invoice is created under Invoice → Drafts &amp; receipts.
                      </p>
                    )}
                    <div className="table-responsive">
                      <table className="table admin-pump-table mb-0">
                        <thead>
                          <tr>
                            <th>Plan</th>
                            <th>Start</th>
                            <th>End</th>
                            <NumTh>Amount</NumTh>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.subscriptions || []).map((sub) => (
                            <tr key={sub.id}>
                              <td>{sub.plan?.plan_name}</td>
                              <td>{sub.subscription_start_date}</td>
                              <td>{sub.subscription_end_date}</td>
                              <NumTd>{sub.amount}</NumTd>
                              <td>{sub.payment_status === 1 ? 'Paid' : 'Pending'}</td>
                              <td>
                                <div className="admin-action-row">
                                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleRenew(sub.id)}>Renew</button>
                                  {sub.payment_status !== 1 && (
                                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => handleMarkPaid(sub.id)}>
                                      Mark receipt
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  </AdminPumpSectionShell>
                )}

                {activeModule === 'alerts' && (
                  <AdminPumpSectionShell title={MODULE_META.alerts.title} description={MODULE_META.alerts.description}>
                  <div className="admin-pump-hub__panel admin-pump-hub__alert-list">
                    {(detail.alerts || []).length === 0 && (
                      <p className="text-muted mb-0">No alerts for this pump — everything looks clear.</p>
                    )}
                    {(detail.alerts || []).map((alert, idx) => (
                      <div key={idx} className={`admin-alert-item ${alert.severity}`}>
                        <strong>{alert.title}</strong>
                        <div className="small mt-1">{alert.message}</div>
                      </div>
                    ))}
                  </div>
                  </AdminPumpSectionShell>
                )}

                {activeModule === 'invoice' && (
                  <AdminPumpSectionShell title={MODULE_META.invoice.title} description={MODULE_META.invoice.description}>
                  <div className="admin-pump-hub__panel">
                    <ul className="nav nav-pills admin-operations-subnav mb-3 flex-wrap">
                      <li className="nav-item">
                        <button type="button" className={`nav-link ${invoiceSection === 'settings' ? 'active' : ''}`} onClick={() => setInvoiceSection('settings')}>
                          Settings
                        </button>
                      </li>
                      <li className="nav-item">
                        <button type="button" className={`nav-link ${invoiceSection === 'invoices' ? 'active' : ''}`} onClick={() => setInvoiceSection('invoices')}>
                          Drafts &amp; client accounting
                        </button>
                      </li>
                    </ul>

                    {invoiceSection === 'settings' && (
                      <>
                        <p className="text-muted small">
                          Configure pump location, place of supply, GST details, and default invoice format.
                        </p>
                        <InvoiceSettingsFormFields values={invoice} errors={invoiceErrors} onChange={handleInvoiceChange} />
                        <button type="button" className="btn btn-primary mt-3" onClick={saveInvoice}>
                          Save invoice settings
                        </button>
                      </>
                    )}

                    {invoiceSection === 'invoices' && (
                      <AdminPumpInvoicesPanel
                        pumpId={pumpId}
                        defaultPlaceCode={invoice.place_supply_code}
                        defaultPlaceName={invoice.place}
                      />
                    )}
                  </div>
                  </AdminPumpSectionShell>
                )}
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPumpDetail;
